import { RefObject, useEffect, useLayoutEffect, useRef } from "react";

interface UseStickyPageTabsProps {
  scrollRef?: RefObject<HTMLElement | null>;
  tabsBarRef: RefObject<HTMLElement | null>;
  pagerRef: RefObject<HTMLElement | null>;
  activeIndex: number;
  stickyTop: number;
  panelsKey: string;
}

interface StickyElements {
  scroller: HTMLElement;
  pager: HTMLElement;
  tabsBar: HTMLElement;
}

interface StickyState {
  activeIndex: number;
  stickyTop: number;
  positions: Record<number, number>;
}

const PIN_TOLERANCE = 1;

const getElements = (
  scrollRef: RefObject<HTMLElement | null> | undefined,
  pagerRef: RefObject<HTMLElement | null>,
  tabsBarRef: RefObject<HTMLElement | null>
): StickyElements | undefined => {
  const scroller = scrollRef?.current;
  const pager = pagerRef.current;
  const tabsBar = tabsBarRef.current;
  return scroller && pager && tabsBar
    ? { scroller, pager, tabsBar }
    : undefined;
};

const getPanels = (pager: HTMLElement) =>
  Array.from(pager.children) as HTMLElement[];

const getCollapseDistance = (
  { scroller, pager, tabsBar }: StickyElements,
  stickyTop: number
) =>
  pager.getBoundingClientRect().top -
  scroller.getBoundingClientRect().top +
  scroller.scrollTop -
  tabsBar.offsetHeight -
  stickyTop;

const setOffset = (panel: HTMLElement | undefined, offset: number) => {
  if (panel) panel.style.transform = offset ? `translateY(${offset}px)` : "";
};

const fitPager = (elements: StickyElements, state: StickyState) => {
  const { scroller, pager, tabsBar } = elements;
  const active = getPanels(pager)[state.activeIndex];
  const minHeight =
    scroller.clientHeight - state.stickyTop - tabsBar.offsetHeight;
  pager.style.height = `${Math.max(minHeight, active?.offsetHeight ?? 0)}px`;
};

const alignInactivePanels = (elements: StickyElements, state: StickyState) => {
  const collapse = getCollapseDistance(elements, state.stickyTop);
  const pinnedBy = elements.scroller.scrollTop - collapse;
  const pinned = pinnedBy >= -PIN_TOLERANCE;
  if (!pinned) state.positions = {};
  getPanels(elements.pager).forEach((panel, index) => {
    if (index === state.activeIndex) return;
    setOffset(panel, pinned ? pinnedBy - (state.positions[index] ?? 0) : 0);
  });
};

const switchPanel = (
  elements: StickyElements,
  state: StickyState,
  previousIndex: number
) => {
  const { scroller, pager } = elements;
  const collapse = getCollapseDistance(elements, state.stickyTop);
  const pinnedBy = scroller.scrollTop - collapse;
  const pinned = pinnedBy >= -PIN_TOLERANCE;
  if (pinned) state.positions[previousIndex] = Math.max(0, pinnedBy);
  setOffset(getPanels(pager)[state.activeIndex], 0);
  fitPager(elements, state);
  if (pinned) {
    scroller.scrollTop = collapse + (state.positions[state.activeIndex] ?? 0);
  }
  alignInactivePanels(elements, state);
};

const useStickyPageTabs = ({
  scrollRef,
  tabsBarRef,
  pagerRef,
  activeIndex,
  stickyTop,
  panelsKey,
}: UseStickyPageTabsProps) => {
  const stateRef = useRef<StickyState>({
    activeIndex,
    stickyTop,
    positions: {},
  });

  useLayoutEffect(() => {
    const state = stateRef.current;
    const previousIndex = state.activeIndex;
    state.activeIndex = activeIndex;
    state.stickyTop = stickyTop;
    const elements = getElements(scrollRef, pagerRef, tabsBarRef);
    if (elements && previousIndex !== activeIndex) {
      switchPanel(elements, state, previousIndex);
    }
  }, [activeIndex, stickyTop, scrollRef, pagerRef, tabsBarRef]);

  useEffect(() => {
    const elements = getElements(scrollRef, pagerRef, tabsBarRef);
    if (!elements) return;
    const onScroll = () => alignInactivePanels(elements, stateRef.current);
    const onResize = () => {
      fitPager(elements, stateRef.current);
      onScroll();
    };
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(elements.scroller);
    getPanels(elements.pager).forEach((panel) => resizeObserver.observe(panel));
    elements.scroller.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      resizeObserver.disconnect();
      elements.scroller.removeEventListener("scroll", onScroll);
    };
  }, [panelsKey, scrollRef, pagerRef, tabsBarRef]);
};

export default useStickyPageTabs;
